// ============================================================
//  DashboardPage.js
//  Dashboard-specific styles are scoped under .dsh-* in
//  DashboardPage.css; the shared .btn / .modal / .form-* classes
//  in that file are used across the app and left untouched.
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  listBots,
  createBot,
  deleteBot,
  toggleBot,
  cloneBot,
  getBillingOverview,
} from '../../api/client';
import toast from 'react-hot-toast';
import PhoneRegistrationWizard from '../bots/PhoneRegistrationWizard';
import './DashboardPage.css';

// ── Helpers ───────────────────────────────────────────────────
function initials(name = '') {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

const BOT_COLORS = ['#0E9F6E', '#2563EB', '#7C3AED', '#DB2777', '#EA580C', '#0891B2', '#4F46E5', '#059669'];
function botColor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return BOT_COLORS[Math.abs(h) % BOT_COLORS.length];
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function fmt(n) {
  return Number(n ?? 0).toLocaleString();
}

// ── Stat card ─────────────────────────────────────────────────
function StatCard({ icon, tone = 'green', label, value, sub, children }) {
  return (
    <div className="dsh-stat">
      <div className="dsh-stat-top">
        <span className={`dsh-stat-ic tone-${tone}`}><i className={`fa-solid ${icon}`} /></span>
        <span className="dsh-stat-label">{label}</span>
      </div>
      <div className="dsh-stat-value">{value}</div>
      {sub && <div className="dsh-stat-sub">{sub}</div>}
      {children}
    </div>
  );
}

// ── Bot card ──────────────────────────────────────────────────
function BotCard({ bot, onToggle, onDelete, onClone, onClick }) {
  const [toggling, setToggling] = useState(false);
  const isActive = bot.bIsActive == 1; // eslint-disable-line eqeqeq

  async function handleToggle(e) {
    e.stopPropagation();
    setToggling(true);
    await onToggle(bot.bId, !isActive);
    setToggling(false);
  }

  function copyId(e) {
    e.stopPropagation();
    navigator.clipboard?.writeText(bot.bId).then(() => toast.success('Bot ID copied')).catch(() => {});
  }

  return (
    <div
      className={`dsh-bot ${isActive ? 'is-live' : 'is-paused'}`}
      onClick={() => onClick(bot.bId)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(bot.bId)}
    >
      <div className="dsh-bot-head">
        <div className="dsh-bot-avatar" style={{ background: botColor(bot.bName || bot.bId) }} aria-hidden="true">
          {initials(bot.bName) || 'B'}
          <span className="dsh-bot-platform" title="WhatsApp"><i className="fa-brands fa-whatsapp" /></span>
        </div>
        <div className="dsh-bot-info">
          <div className="dsh-bot-name">{bot.bName}</div>
          <button type="button" className="dsh-bot-id" onClick={copyId} title="Copy bot ID">
            #{bot.bId} <i className="fa-regular fa-copy" />
          </button>
        </div>
        <span
          className="toggle-wrap"
          onClick={handleToggle}
          role="switch"
          aria-checked={isActive}
          aria-label={`${isActive ? 'Pause' : 'Activate'} ${bot.bName}`}
          title={isActive ? 'Pause bot' : 'Activate bot'}
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleToggle(e)}
        >
          <span className={`toggle-track ${isActive ? 'on' : 'off'} ${toggling ? 'toggling' : ''}`}>
            <span className="toggle-knob" />
          </span>
        </span>
      </div>

      <div className="dsh-bot-status">
        <span className={`dsh-pill ${isActive ? 'pill-live' : 'pill-paused'}`}>
          <span className="dsh-pill-dot" /> {isActive ? 'Live' : 'Paused'}
        </span>
        {bot.bPaymentIssue == 1 && ( // eslint-disable-line eqeqeq
          <span className="dsh-pill pill-warn"><i className="fa-solid fa-triangle-exclamation" /> Billing issue</span>
        )}
      </div>

      <div className="dsh-bot-metrics">
        <div>
          <b>{fmt(bot.totalCommands)}</b>
          <span>Commands</span>
        </div>
        <div>
          <b>{fmt(bot.totalUsers)}</b>
          <span>Users</span>
        </div>
      </div>

      <div className="dsh-bot-foot" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="dsh-bot-open" onClick={() => onClick(bot.bId)}>
          Open bot <i className="fa-solid fa-arrow-right" />
        </button>
        <div className="dsh-bot-tools">
          <button type="button" className="dsh-icon-btn" onClick={() => onClone(bot.bId)} title="Clone bot" aria-label={`Clone ${bot.bName}`}>
            <i className="fa-regular fa-clone" />
          </button>
          <button type="button" className="dsh-icon-btn danger" onClick={() => onDelete(bot.bId, bot.bName)} title="Delete bot" aria-label={`Delete ${bot.bName}`}>
            <i className="fa-regular fa-trash-can" />
          </button>
        </div>
      </div>
    </div>
  );
}

function BotSkeleton() {
  return (
    <div className="dsh-bot dsh-bot-skel" aria-hidden="true">
      <div className="dsh-bot-head"><span className="sk sk-av" /><div className="dsh-bot-info"><span className="sk sk-line" /><span className="sk sk-line short" /></div></div>
      <div className="dsh-bot-metrics"><div><span className="sk sk-num" /></div><div><span className="sk sk-num" /></div></div>
    </div>
  );
}

// ── Create Bot modal ──────────────────────────────────────────
function CreateBotModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    bName: '',
    bLanguage: 'json',
    bToken: '',
    bPhoneNumberId: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPhoneWizard, setShowPhoneWizard] = useState(false);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.bName.trim())          return toast.error('Bot name is required');
    if (!form.bToken.trim())         return toast.error('WhatsApp token is required');
    if (!form.bPhoneNumberId.trim()) return toast.error('Phone number ID is required');

    setLoading(true);
    try {
      const res = await createBot({ ...form, bPlatform: 'whatsapp' });
      if (res.data?.success) {
        toast.success('Bot created!');
        onCreated(res.data.bId);
      } else {
        toast.error(res.data?.message ?? 'Failed to create bot');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setLoading(false);
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <>
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-modal-title"
    >
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title" id="create-modal-title">Create new bot</div>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body" noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="bName">Bot name</label>
            <input
              id="bName"
              className="form-input"
              placeholder="e.g. Customer support bot"
              value={form.bName}
              onChange={(e) => set('bName', e.target.value)}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Platform</label>
            <div
              className="form-input"
              style={{ background: 'var(--surface)', color: 'var(--text-muted)', cursor: 'default' }}
            >
              WhatsApp (Cloud API)
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="bLanguage">Default command language</label>
            <select
              id="bLanguage"
              className="form-input form-select"
              value={form.bLanguage}
              onChange={(e) => set('bLanguage', e.target.value)}
            >
              <option value="json">JSON</option>
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
            </select>
            <div className="form-hint">
              Pre-selects this language whenever you create a new Command on this bot. You can still pick a different language per command, and Custom Endpoints have their own separate language picker.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="bToken">WhatsApp token</label>
            <input
              id="bToken"
              className="form-input"
              placeholder="EAAxxxx…"
              value={form.bToken}
              onChange={(e) => set('bToken', e.target.value)}
            />
            <div className="form-hint">
              From Meta Business → WhatsApp → API Setup
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="bPhoneNumberId">Phone number ID</label>
            <input
              id="bPhoneNumberId"
              className="form-input"
              placeholder="123456789012345"
              value={form.bPhoneNumberId}
              onChange={(e) => set('bPhoneNumberId', e.target.value)}
            />
            <div className="form-hint">
              Found next to your number in WhatsApp API Setup —{' '}
              <button type="button" className="link-btn" style={{ fontSize: 12, padding: 0 }}
                onClick={() => setShowPhoneWizard(true)}>
                don't have a number yet? Register one →
              </button>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <><span className="spinner" aria-hidden="true" /> Creating…</>
              ) : (
                'Create bot'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>

    {showPhoneWizard && (
      <PhoneRegistrationWizard
        initialToken={form.bToken}
        onClose={() => setShowPhoneWizard(false)}
        onComplete={({ phoneNumberId, token }) => {
          setForm(f => ({ ...f, bPhoneNumberId: phoneNumberId, bToken: token || f.bToken }));
          setShowPhoneWizard(false);
          toast.success('Number added — continue creating your bot below.');
        }}
      />
    )}
    </>
  );
}

// ── Delete confirm modal ──────────────────────────────────────
function DeleteModal({ botName, onConfirm, onClose }) {
  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
    >
      <div className="modal modal-sm">
        <div className="modal-header">
          <div className="modal-title" id="delete-modal-title">Delete bot</div>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body">
          <div className="delete-warning">
            This will permanently delete <strong>{botName}</strong> along with all
            its commands, panel fields, and logs. This cannot be undone.
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-danger" onClick={onConfirm}>Delete bot</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function DashboardPage() {
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const [bots, setBots]                 = useState([]);
  const [stats, setStats]               = useState(null);
  const [plan, setPlan]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filter, setFilter]             = useState('all');
  const [creating, setCreating]         = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [botsRes, billingRes] = await Promise.all([
        listBots(),
        getBillingOverview(),
      ]);
      if (botsRes.data?.success) {
        setBots(botsRes.data.bots ?? []);
        setStats(botsRes.data.stats ?? null);
      }
      if (billingRes.data?.current_plan) {
        setPlan(billingRes.data.current_plan);
      }
    } catch {
      /* axios interceptor handles 401 */
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived values ────────────────────────────────────────
  const maxBots  = plan?.plMaxBots != null ? Number(plan.plMaxBots) : null;
  const atLimit  = maxBots !== null && bots.length >= maxBots;
  const planName = plan?.plName ?? 'Free';
  const usagePct = maxBots ? Math.min(100, Math.round((bots.length / maxBots) * 100)) : 0;

  /* eslint-disable eqeqeq */
  const billingIssueBots = bots.filter((b) => b.bPaymentIssue == 1);
  const activeCount   = stats?.active   ?? bots.filter(b => b.bIsActive == 1).length;
  const inactiveCount = stats?.inactive ?? bots.filter(b => b.bIsActive != 1).length;
  const totalCount    = stats?.total    ?? bots.length;
  const totalUsers    = stats?.totalUsers ?? bots.reduce((s, b) => s + Number(b.totalUsers ?? 0), 0);

  const counts = { all: bots.length, active: bots.filter(b => b.bIsActive == 1).length, inactive: bots.filter(b => b.bIsActive != 1).length };

  const filtered = bots.filter((b) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      b.bName?.toLowerCase().includes(q) ||
      b.bId?.toLowerCase().includes(q);
    const matchFilter =
      filter === 'all' ||
      (filter === 'active'   &&  b.bIsActive == 1) ||
      (filter === 'inactive' &&  b.bIsActive != 1);
    return matchSearch && matchFilter;
  });
  /* eslint-enable eqeqeq */

  // ── Handlers ──────────────────────────────────────────────
  async function handleToggle(bId, active) {
    try {
      await toggleBot(bId, active);
      setBots((prev) =>
        prev.map((b) => (b.bId === bId ? { ...b, bIsActive: active ? 1 : 0 } : b))
      );
      setStats((s) => s ? { ...s, active: s.active + (active ? 1 : -1), inactive: s.inactive + (active ? -1 : 1) } : s);
    } catch {
      toast.error('Failed to update bot status');
    }
  }

  function handleDelete(bId, bName) {
    setDeleteTarget({ bId, bName });
  }

  async function confirmDelete() {
    try {
      await deleteBot(deleteTarget.bId);
      setBots((prev) => prev.filter((b) => b.bId !== deleteTarget.bId));
      toast.success('Bot deleted');
      load();
    } catch {
      toast.error('Failed to delete bot');
    }
    setDeleteTarget(null);
  }

  async function handleClone(bId) {
    toast.loading('Cloning bot…', { id: 'clone' });
    try {
      const res = await cloneBot(bId);
      if (res.data?.success) {
        toast.success('Bot cloned!', { id: 'clone' });
        load();
      } else {
        toast.error(res.data?.message ?? 'Clone failed', { id: 'clone' });
      }
    } catch {
      toast.error('Network error', { id: 'clone' });
    }
  }

  function handleCreated(bId) {
    setCreating(false);
    navigate(`/bots/${bId}`);
  }

  function handleCreateClick() {
    if (atLimit) {
      toast.error(
        `Your ${planName} plan allows up to ${maxBots} bot${maxBots === 1 ? '' : 's'}. Upgrade to add more.`,
        { duration: 5000 }
      );
      navigate('/billing');
      return;
    }
    setCreating(true);
  }

  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

  const SHORTCUTS = [
    { icon: 'fa-inbox',        label: 'Agent inbox',       sub: 'Reply to customers',        to: '/inbox' },
    { icon: 'fa-book-open',    label: 'How to use',        sub: 'Guides & action reference', to: '/how-to-use' },
    { icon: 'fa-circle-check', label: 'Meta verification', sub: 'Get your business verified', to: '/meta-verification' },
    { icon: 'fa-credit-card',  label: 'Billing',           sub: `${planName} plan`,          to: '/billing' },
  ];

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="dsh">

      {/* Header */}
      <header className="dsh-head">
        <div>
          <p className="dsh-date">{today}</p>
          <h1 className="dsh-title">{greeting()}{user?.sFname ? `, ${user.sFname}` : ''}</h1>
          <p className="dsh-sub">Here’s how your WhatsApp bots are doing.</p>
        </div>
        <div className="dsh-head-actions">
          <button type="button" className="dsh-plan-chip" onClick={() => navigate('/billing')} title="Manage plan">
            <i className="fa-solid fa-gem" /> {planName}
            {maxBots !== null && <span>{bots.length}/{maxBots}</span>}
          </button>
          <button
            type="button"
            className={`dsh-btn ${atLimit ? 'dsh-btn-ghost' : 'dsh-btn-primary'}`}
            onClick={handleCreateClick}
            title={atLimit ? `Upgrade to create more than ${maxBots} bots` : 'Create a new bot'}
          >
            {atLimit
              ? <><i className="fa-solid fa-lock" /> Upgrade to add bots</>
              : <><i className="fa-solid fa-plus" /> New bot</>}
          </button>
        </div>
      </header>

      {/* Alerts */}
      {billingIssueBots.length > 0 && (
        <div className="dsh-alert dsh-alert-danger" role="alert">
          <span className="dsh-alert-ic"><i className="fa-solid fa-triangle-exclamation" /></span>
          <div className="dsh-alert-body">
            <strong>WhatsApp payment issue — messages are not being delivered</strong>
            <p>
              {billingIssueBots.length === 1
                ? `Your bot "${billingIssueBots[0].bName}" has an unsettled payment on its WhatsApp Business account. Meta is blocking outbound template messages until this is resolved.`
                : `${billingIssueBots.length} of your bots have an unsettled payment on their WhatsApp Business account. Meta is blocking outbound template messages until this is resolved.`}
            </p>
          </div>
          <a className="dsh-btn dsh-btn-danger" href="https://business.facebook.com/billing_hub" target="_blank" rel="noopener noreferrer">
            Resolve on Meta <i className="fa-solid fa-arrow-up-right-from-square" />
          </a>
        </div>
      )}

      {atLimit && (
        <div className="dsh-alert dsh-alert-warn" role="alert">
          <span className="dsh-alert-ic"><i className="fa-solid fa-gauge-high" /></span>
          <div className="dsh-alert-body">
            <strong>{planName} plan limit reached</strong>
            <p>You’ve used all {maxBots} bot slot{maxBots === 1 ? '' : 's'}. Upgrade to create more bots.</p>
          </div>
          <button type="button" className="dsh-btn dsh-btn-dark" onClick={() => navigate('/billing')}>
            See plans <i className="fa-solid fa-arrow-right" />
          </button>
        </div>
      )}

      {/* Stats */}
      <section className="dsh-stats" aria-label="Overview">
        {loading ? (
          [0, 1, 2, 3].map(i => <div key={i} className="dsh-stat"><span className="sk sk-line short" /><span className="sk sk-big" /></div>)
        ) : (
          <>
            <StatCard icon="fa-robot" tone="green" label="Total bots" value={fmt(totalCount)} sub={`${fmt(activeCount)} live · ${fmt(inactiveCount)} paused`} />
            <StatCard icon="fa-signal" tone="teal" label="Live now" value={fmt(activeCount)}
              sub={totalCount ? `${Math.round((activeCount / Math.max(totalCount, 1)) * 100)}% of your bots` : 'No bots yet'} />
            <StatCard icon="fa-users" tone="blue" label="Total users" value={fmt(totalUsers)} sub="Across all bots" />
            <StatCard icon="fa-gem" tone={atLimit ? 'red' : 'violet'} label={`${planName} plan`}
              value={maxBots !== null ? <>{bots.length}<small>/{maxBots}</small></> : bots.length}
              sub={maxBots !== null ? (atLimit ? 'Limit reached' : `${maxBots - bots.length} bot slot${maxBots - bots.length === 1 ? '' : 's'} left`) : 'Bots created'}>
              {maxBots !== null && (
                <div className={`dsh-meter ${atLimit ? 'is-full' : ''}`}><span style={{ width: `${usagePct}%` }} /></div>
              )}
            </StatCard>
          </>
        )}
      </section>

      {/* Bots */}
      <section className="dsh-section">
        <div className="dsh-section-head">
          <div>
            <h2>Your bots</h2>
            <p>{bots.length ? `${bots.length} bot${bots.length === 1 ? '' : 's'} on your account` : 'Create a bot to start automating WhatsApp'}</p>
          </div>
          {bots.length > 0 && (
            <div className="dsh-toolbar">
              <label className="dsh-search">
                <i className="fa-solid fa-magnifying-glass" />
                <input
                  type="search"
                  placeholder="Search by name or ID"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search bots"
                />
              </label>
              <div className="dsh-seg" role="group" aria-label="Filter bots">
                {[['all', 'All'], ['active', 'Live'], ['inactive', 'Paused']].map(([k, l]) => (
                  <button key={k} type="button" className={filter === k ? 'is-active' : ''} onClick={() => setFilter(k)} aria-pressed={filter === k}>
                    {l} <em>{counts[k]}</em>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="dsh-grid">{[0, 1, 2].map(i => <BotSkeleton key={i} />)}</div>
        ) : bots.length === 0 ? (
          <div className="dsh-onboard">
            <div className="dsh-onboard-copy">
              <span className="dsh-onboard-ic"><i className="fa-brands fa-whatsapp" /></span>
              <h3>Launch your first WhatsApp bot</h3>
              <p>Connect your WhatsApp Business number, add a few commands, and your bot starts replying to customers right away.</p>
              <button type="button" className="dsh-btn dsh-btn-primary" onClick={handleCreateClick}>
                <i className="fa-solid fa-plus" /> Create your first bot
              </button>
            </div>
            <ol className="dsh-onboard-steps">
              <li><span>1</span><div><b>Connect your number</b><p>Paste your WhatsApp token and phone number ID from Meta.</p></div></li>
              <li><span>2</span><div><b>Build commands & flows</b><p>Use the visual builder or write JSON actions.</p></div></li>
              <li><span>3</span><div><b>Go live</b><p>Switch the bot on and watch conversations roll in.</p></div></li>
            </ol>
          </div>
        ) : filtered.length === 0 ? (
          <div className="dsh-empty">
            <i className="fa-solid fa-magnifying-glass" />
            <b>No bots match</b>
            <span>Try another name or clear the filter.</span>
            <button type="button" className="dsh-link" onClick={() => { setSearch(''); setFilter('all'); }}>Clear filters</button>
          </div>
        ) : (
          <div className="dsh-grid">
            {filtered.map((bot) => (
              <BotCard
                key={bot.bId}
                bot={bot}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onClone={handleClone}
                onClick={(bId) => navigate(`/bots/${bId}`)}
              />
            ))}
            {!atLimit && !search && filter === 'all' && (
              <button type="button" className="dsh-add-card" onClick={handleCreateClick}>
                <span><i className="fa-solid fa-plus" /></span>
                <b>New bot</b>
                <em>Connect another number</em>
              </button>
            )}
          </div>
        )}
      </section>

      {/* Shortcuts */}
      <section className="dsh-section">
        <div className="dsh-section-head"><div><h2>Shortcuts</h2></div></div>
        <div className="dsh-shortcuts">
          {SHORTCUTS.map(s => (
            <button key={s.to} type="button" className="dsh-shortcut" onClick={() => navigate(s.to)}>
              <span className="dsh-shortcut-ic"><i className={`fa-solid ${s.icon}`} /></span>
              <span className="dsh-shortcut-txt"><b>{s.label}</b><em>{s.sub}</em></span>
              <i className="fa-solid fa-chevron-right dsh-shortcut-go" />
            </button>
          ))}
        </div>
      </section>

      {/* Modals */}
      {creating && (
        <CreateBotModal
          onClose={() => setCreating(false)}
          onCreated={handleCreated}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          botName={deleteTarget.bName}
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
